import { faArrowRightFromLine } from "@fortawesome/pro-solid-svg-icons";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useCallback, useRef } from "react";

import { PRIMARY_NAVIGATION_TOGGLE_WIDTH } from "@/components/nav/constants";
import { useNavigationStore } from "@/components/nav/state";
import { Icon } from "@/components/ui-v2/Icon/Icon";
import { IconButton } from "@/components/ui-v2/IconButton/IconButton";

const NavigationToggleContainer = styled(Box)(() => ({
  width: PRIMARY_NAVIGATION_TOGGLE_WIDTH,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  flexGrow: 0,

  // this animation ensures that the menu doesn't open back up from the "onPointerOver" listener as it's collapsing
  animation: "fadeIn 0.3s",
  "@keyframes fadeIn": {
    "0%, 99%": {
      pointerEvents: "none",
    },
    "100%": {
      pointerEvents: "auto",
    },
  },
}));

const NavigationToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary[3],

  "&:hover": {
    "& > svg": {
      color: theme.palette.secondary[1],
    },
  },
}));

export const PrimaryNavigationToggle = () => {
  const {
    primaryNavigationVariant,
    makePrimaryNavigationPermanent,
    expandPrimaryNavigation,
  } = useNavigationStore();
  const delayHandlerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const expandPrimaryNavigationWithDelay = useCallback(() => {
    // Add a slight delay to prevent accidental expansions
    delayHandlerRef.current = setTimeout(() => {
      expandPrimaryNavigation();
    }, 300);
  }, [expandPrimaryNavigation]);
  const clearDelayHandler = useCallback(() => {
    if (delayHandlerRef.current) {
      clearTimeout(delayHandlerRef.current);
      delayHandlerRef.current = null;
    }
  }, []);
  const onClickExpand = useCallback(() => {
    clearDelayHandler();
    makePrimaryNavigationPermanent();
  }, [clearDelayHandler, makePrimaryNavigationPermanent]);

  if (primaryNavigationVariant !== "temporary") {
    // If the primary navigation is not temporary, do not render the toggle button
    return null;
  }

  return (
    <NavigationToggleContainer>
      <NavigationToggleButton
        size="medium"
        aria-label="Click to Expand Sidebar"
        onClick={onClickExpand}
        onPointerEnter={expandPrimaryNavigationWithDelay}
        onPointerLeave={clearDelayHandler}
        variant="quaternary"
        tooltipProps={{
          placement: "right",
        }}
        data-testid="expand-primary-navigation"
      >
        <Icon icon={faArrowRightFromLine} size="large" />
      </NavigationToggleButton>
    </NavigationToggleContainer>
  );
};
